import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

/**
 * Theme management for Design System v1 (§7.6).
 *
 * - Defaults to the OS preference (`system`) on first load.
 * - A user override is persisted to localStorage under `kyck-theme`.
 * - Applies `class="dark"` to <html> so the token swap in index.css
 *   (the `.dark` block + `@custom-variant dark`) takes effect.
 *
 * Deliberately hand-rolled rather than pulling in `next-themes` — this is
 * a Vite/React app (no RSC) and the project keeps its dependency surface
 * small (CLAUDE.md: React built-ins only).
 *
 * NOTE — dark mode is intentionally DISABLED in the UI until Phase 3
 * (`THEME_UI_ENABLED = false`). While disabled the provider forces light,
 * ignores the OS preference, and clears any persisted override (so anyone who
 * already toggled dark reverts to light on next load). The toggle behaviour
 * stays fully built and tested via the `enabled` prop; flip the flag to
 * re-enable once the layout flips cleanly. See CLAUDE.md "Design System v1".
 */

export type Theme = 'dark' | 'light' | 'system';
export type ResolvedTheme = 'dark' | 'light';

const STORAGE_KEY = 'kyck-theme';

/** Master switch for dark mode in the UI. Flip to `true` in Phase 3. */
const THEME_UI_ENABLED = false;

type ThemeProviderState = {
  /** The user's selected preference (may be `system`). */
  theme: Theme;
  /** The concrete mode currently applied to <html> (never `system`). */
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
};

const ThemeProviderContext = createContext<ThemeProviderState | undefined>(undefined);

function systemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readStoredTheme(fallback: Theme): Theme {
  if (typeof localStorage === 'undefined') return fallback;
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : fallback;
}

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  enabled = THEME_UI_ENABLED,
}: {
  children: ReactNode;
  defaultTheme?: Theme;
  /** When false, dark mode is forced off in the UI (see THEME_UI_ENABLED). */
  enabled?: boolean;
}) {
  const [theme, setThemeState] = useState<Theme>(() =>
    enabled ? readStoredTheme(defaultTheme) : 'light',
  );
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    if (!enabled) return 'light';
    const initial = readStoredTheme(defaultTheme);
    return initial === 'system' ? systemTheme() : (initial as ResolvedTheme);
  });

  useEffect(() => {
    const root = window.document.documentElement;

    if (!enabled) {
      // Forced light: drop any dark class and clear the persisted override so a
      // previously-dark user reverts. A `class="dark"` added manually via
      // DevTools AFTER mount is left alone — devs can still preview dark mode.
      root.classList.remove('dark');
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // storage disabled — nothing to clear
      }
      setResolvedTheme('light');
      return;
    }

    const apply = () => {
      const resolved = theme === 'system' ? systemTheme() : theme;
      root.classList.toggle('dark', resolved === 'dark');
      setResolvedTheme(resolved);
    };

    apply();

    // Live-follow the OS when the user has chosen `system`.
    if (theme === 'system' && window.matchMedia) {
      const media = window.matchMedia('(prefers-color-scheme: dark)');
      media.addEventListener('change', apply);
      return () => media.removeEventListener('change', apply);
    }
  }, [theme, enabled]);

  const setTheme = useCallback(
    (next: Theme) => {
      // No-op while dark mode is disabled in the UI.
      if (!enabled) return;
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Private mode / storage disabled — keep the in-memory preference.
      }
      setThemeState(next);
    },
    [enabled],
  );

  return (
    <ThemeProviderContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export function useTheme(): ThemeProviderState {
  const context = useContext(ThemeProviderContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
